/**
 * ActivateAccountPage — Public page for drivers to set their password
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, CheckCircle, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import config from 'config';

const API_URL = config.API_URL;

export default function ActivateAccountPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Lien d\'activation invalide');
      setLoading(false);
      return;
    }
    axios.get(`${API_URL}/api/auth/activation-info?token=${token}`)
      .then(res => { setInfo(res.data); setLoading(false); })
      .catch(err => { setError(err.response?.data?.detail || 'Lien invalide ou expiré'); setLoading(false); });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (password !== passwordConfirm) { setSubmitError('Les mots de passe ne correspondent pas'); return; }
    if (password.length < 8) { setSubmitError('Min. 8 caractères'); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/auth/activate-account`, { token, password, password_confirm: passwordConfirm });
      setSuccess(true);
    } catch (err) {
      setSubmitError(err.response?.data?.detail || 'Erreur lors de l\'activation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center p-4" data-testid="activate-account-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#C9A24D] font-serif">Y GO</h1>
          <p className="text-[#E5E5E5]/40 text-xs tracking-widest mt-1">SHUTTLE SERVICES FRANCE</p>
        </div>

        <div className="bg-[#1F1F1F] rounded-xl border border-[#C9A24D]/20 p-6">
          {loading && (
            <div className="flex flex-col items-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-[#C9A24D]" />
              <p className="text-[#E5E5E5]/60 text-sm mt-3">Vérification du lien...</p>
            </div>
          )}

          {error && !loading && (
            <div className="text-center py-8" data-testid="activation-error">
              <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <h2 className="text-[#E5E5E5] text-lg font-semibold mb-2">Lien invalide</h2>
              <p className="text-[#E5E5E5]/60 text-sm">{error}</p>
              <Button onClick={() => navigate('/login')} className="mt-6 bg-[#C9A24D] text-black hover:bg-[#E6C77A]">
                Aller à la connexion
              </Button>
            </div>
          )}

          {success && (
            <div className="text-center py-8" data-testid="activation-success">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <h2 className="text-[#E5E5E5] text-lg font-semibold mb-2">Compte activé !</h2>
              <p className="text-[#E5E5E5]/60 text-sm">Votre mot de passe a été défini. Vous pouvez maintenant vous connecter.</p>
              <Button onClick={() => navigate('/login')} className="mt-6 bg-[#C9A24D] text-black hover:bg-[#E6C77A]" data-testid="go-to-login-btn">
                Se connecter
              </Button>
            </div>
          )}

          {info && !success && !error && (
            <form onSubmit={handleSubmit}>
              <h2 className="text-[#C9A24D] text-lg font-semibold mb-1">Activez votre compte</h2>
              <p className="text-[#E5E5E5]/60 text-sm mb-6">
                Bienvenue <span className="text-white font-medium">{info.first_name} {info.last_name}</span>, définissez votre mot de passe pour accéder à votre espace chauffeur.
              </p>

              {submitError && <p className="text-red-400 text-sm bg-red-500/10 p-2 rounded mb-4" data-testid="activation-submit-error">{submitError}</p>}

              <div className="space-y-4">
                <div>
                  <Label className="text-[#E5E5E5]/70 text-xs">Mot de passe</Label>
                  <div className="relative mt-1">
                    <Input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                      placeholder="Min. 8 car., 1 maj., 1 chiffre" className="bg-[#0B0B0B] border-white/15 text-white pr-10" data-testid="activation-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-white">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-[#E5E5E5]/70 text-xs">Confirmez le mot de passe</Label>
                  <Input type="password" value={passwordConfirm} onChange={e => setPasswordConfirm(e.target.value)}
                    className="bg-[#0B0B0B] border-white/15 text-white mt-1" data-testid="activation-password-confirm" />
                </div>
                <Button type="submit" disabled={submitting || !password || !passwordConfirm} className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-black font-medium" data-testid="activation-submit-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Activer mon compte
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
